import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteData } from '../contexts/SiteDataContext';

const BlogSection = () => {
  const { blog: blogPosts } = useSiteData();
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section id="blog" ref={ref} className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            ब्लॉग और लेख
          </h2>
          <p 
            className="text-xl text-orange-600 font-semibold"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            राष्ट्रचिंतन • नेतृत्व • संस्कृति
          </p>
          <p className="text-gray-600 mt-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>राष्ट्र निर्माण, नेतृत्व और विकसित भारत 2047 पर विचार</p>
        </motion.div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="group"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 h-full flex flex-col">
                {/* Featured Image */}
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Date */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <Calendar className="h-4 w-4 text-orange-500" />
                    <span style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{post.date}</span>
                  </div>

                  {/* Title */}
                  <h3 
                    className="text-xl font-bold text-blue-900 mb-3 group-hover:text-orange-600 transition-colors"
                    style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                  >
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p 
                    className="text-gray-700 leading-relaxed mb-4 flex-1"
                    style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                  >
                    {post.excerpt}
                  </p>

                  {/* Read More Link */}
                  <Button 
                    variant="link" 
                    className="text-orange-600 hover:text-orange-700 p-0 group-hover:gap-2 transition-all"
                    style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                  >
                    और पढ़ें <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-12"
        >
          <Button 
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:scale-105 transition-all duration-300"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            सभी लेख देखें <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;